import {
  Alert,
  AlertDescription,
  AlertTitle,
  Center,
  Heading,
  HStack,
  Spinner,
  Stack,
  Tag,
  VStack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { ErrorBoundary } from '@sentry/react';
import useDebounce from 'hooks/useDebounce';
import React, { Suspense, useEffect } from 'react';
import { notEmpty } from 'utils/NotEmptyFilter';
import { SearchResult } from './components/SearchResult';
import {
  SearchReference,
  usePreloadedSearchQuery,
  useSearchQueryLoader,
} from './searchQuery';
import {
  searchQuery$variables,
  SearchTypeFilter,
} from './__generated__/searchQuery.graphql';

interface SearchResultsProps {
  query: string;
  spaceIds: string[];
  types: SearchTypeFilter[];
}

export const SearchResults = ({
  query,
  spaceIds,
  types,
}: SearchResultsProps) => {
  const [searchRef, doSearch, clearSearchResults] = useSearchQueryLoader();
  const searchQuery = useDebounce(query);
  const showSpinner = query !== searchQuery;

  useEffect(() => {
    if (searchQuery === '') {
      clearSearchResults();
    } else {
      clearSearchResults();
      let searchVariables: searchQuery$variables = {
        query: searchQuery,
        types,
      };

      if (spaceIds.length > 0) {
        searchVariables.spaceIds = spaceIds;
      }

      doSearch(searchVariables, {
        fetchPolicy: 'network-only',
      });
    }
  }, [searchQuery, spaceIds.join(','), types.join(',')]);

  const spinner = <SearchSpinner />;
  if (showSpinner) {
    return spinner;
  }
  if (searchRef) {
    return (
      <ErrorBoundary fallback={<SearchError />}>
        <Suspense fallback={spinner}>
          <List searchRef={searchRef} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return null;
};

interface ListProps {
  searchRef: SearchReference;
}

const List = ({ searchRef }: ListProps) => {
  const {
    search: { nodes: unfilteredNodes, totalCount },
  } = usePreloadedSearchQuery(searchRef);

  const nodes = unfilteredNodes
    .filter(notEmpty)
    .filter((node) => node.node.publishState !== 'DELETED');

  return (
    <Stack>
      <HStack>
        <Heading fontSize="sm">{t`Results`}</Heading>
        <Tag size="sm" borderRadius="full" data-qa="search-result-count">
          {totalCount}
        </Tag>
      </HStack>
      {nodes.map((node, id) => (
        <SearchResult key={id} result={node} />
      ))}
    </Stack>
  );
};

const SearchError = () => (
  <Alert status="error">
    <VStack align="start">
      <AlertTitle>{t`Search failed`}</AlertTitle>
      <AlertDescription>
        {t`Search appears to be down. You can try again in a couple of minutes.`}
      </AlertDescription>
    </VStack>
  </Alert>
);

const SearchSpinner = () => {
  return (
    <Center>
      <Spinner />
    </Center>
  );
};
